import { STORAGE_KEYS } from '@shared/constants';

/**
 * Persistent storage abstraction for Office Add-ins.
 *
 * Uses OfficeRuntime.storage (persists independently of WebView lifecycle) when
 * running inside Outlook, and falls back to localStorage for dev/standalone mode.
 */
class StorageService {
  private _cachedToken: string | null = null;

  get cachedToken(): string | null {
    return this._cachedToken;
  }

  private get officeStorage(): typeof OfficeRuntime.storage | null {
    return typeof OfficeRuntime !== 'undefined' ? OfficeRuntime.storage : null;
  }

  async getItem(key: string): Promise<string | null> {
    const storage = this.officeStorage;
    if (storage) {
      try {
        const value = await storage.getItem(key);
        return value ?? null;
      } catch {
        return localStorage.getItem(key);
      }
    }
    return localStorage.getItem(key);
  }

  async setItem(key: string, value: string): Promise<void> {
    const storage = this.officeStorage;
    if (storage) {
      try {
        await storage.setItem(key, value);
        return;
      } catch {
        // fall through to localStorage
      }
    }
    localStorage.setItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    const storage = this.officeStorage;
    if (storage) {
      try {
        await storage.removeItem(key);
        return;
      } catch {
        // fall through to localStorage
      }
    }
    localStorage.removeItem(key);
  }

  async initTokenCache(): Promise<void> {
    this._cachedToken = await this.getItem(STORAGE_KEYS.ActiveToken);
  }

  async setToken(token: string): Promise<void> {
    this._cachedToken = token;
    await this.setItem(STORAGE_KEYS.ActiveToken, token);
  }

  async removeToken(): Promise<void> {
    this._cachedToken = null;
    await this.removeItem(STORAGE_KEYS.ActiveToken);
  }

  // Singleton
  public static instance: StorageService | null;

  static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }
}

export const storageService = StorageService.getInstance();
